import { useState, useEffect, useRef } from "react";
import { BREAKPOINTS } from "../constants/messages.constants";

interface UseResponsiveReturn {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  showSidebar: boolean;
  showDetails: boolean;
  setShowSidebar: (show: boolean) => void;
  setShowDetails: (show: boolean) => void;
  handleChatSelect: () => void;
  handleBackToList: () => void;
  toggleDetails: () => void;
}

export const useResponsive = (): UseResponsiveReturn => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);
  const [showSidebar, setShowSidebar] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  // Track if user has manually changed panel visibility
  const userInteractedRef = useRef(false);
  const lastBreakpointRef = useRef<"mobile" | "tablet" | "desktop">("desktop");

  useEffect(() => {
    const checkResponsive = () => {
      const width = window.innerWidth;
      const mobile = width < BREAKPOINTS.MOBILE;
      const tablet = width >= BREAKPOINTS.MOBILE && width < BREAKPOINTS.TABLET;
      const desktop = width >= BREAKPOINTS.TABLET;

      // Determine current breakpoint
      let currentBreakpoint: "mobile" | "tablet" | "desktop" = "desktop";
      if (mobile) currentBreakpoint = "mobile";
      else if (tablet) currentBreakpoint = "tablet";

      // Only update if breakpoint actually changed (not just resize within same breakpoint)
      if (currentBreakpoint !== lastBreakpointRef.current) {
        console.log(
          "📱 Breakpoint changed:",
          lastBreakpointRef.current,
          "→",
          currentBreakpoint
        );

        setIsMobile(mobile);
        setIsTablet(tablet);
        setIsDesktop(desktop);

        // Only reset panels if user hasn't manually interacted
        if (!userInteractedRef.current) {
          if (desktop) {
            setShowSidebar(true);
            setShowDetails(true);
          } else if (tablet) {
            setShowSidebar(true);
            setShowDetails(false);
          } else {
            // Mobile: Keep current state, don't force reset
            setShowSidebar(true);
            setShowDetails(false);
          }
        }

        lastBreakpointRef.current = currentBreakpoint;
      } else {
        // Same breakpoint, just update detection flags without changing panels
        setIsMobile(mobile);
        setIsTablet(tablet);
        setIsDesktop(desktop);
      }
    };

    // Initial check
    checkResponsive();

    // Debounce resize events to avoid excessive calls on mobile
    let resizeTimer: NodeJS.Timeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(checkResponsive, 150);
    };

    window.addEventListener("resize", debouncedResize);
    return () => {
      window.removeEventListener("resize", debouncedResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  const handleChatSelect = () => {
    console.log("📩 Chat selected, isMobile:", isMobile);
    if (isMobile) {
      userInteractedRef.current = true;
      setShowSidebar(false);
      setShowDetails(false);
    }
  };

  const handleBackToList = () => {
    console.log("⬅️ Back to list, isMobile:", isMobile);
    if (isMobile) {
      userInteractedRef.current = true;
      setShowSidebar(true);
      setShowDetails(false);
    }
  };

  const toggleDetails = () => {
    userInteractedRef.current = true;
    setShowDetails((prev) => !prev);
  };

  return {
    isMobile,
    isTablet,
    isDesktop,
    showSidebar,
    showDetails,
    setShowSidebar,
    setShowDetails,
    handleChatSelect,
    handleBackToList,
    toggleDetails,
  };
};
